import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardText, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NewOnboardingDialog } from "@/components/onboarding/NewOnboardingDialog";
import { TermsEditor } from "@/components/onboarding/TermsEditor";
import { fullLink } from "@/components/onboarding/CopyLink";
import { money } from "@/components/onboarding/LeadsSection";

export const STATUS = {
  sent: ["Waiting for buyer", "bg-zinc-200 text-zinc-800"],
  submitted: ["Needs your approval", "bg-amber-300 text-black"],
  live: ["Live", "bg-emerald-600 text-white"],
};

export const StatusChip = ({ s, testId }) => (
  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS[s]?.[1]}`} data-testid={testId}>{STATUS[s]?.[0] || s}</span>
);

export default function AdminOnboarding() {
  const { user } = useAuth();
  const ok = user?.org_type === "internal" && ["super_admin", "admin"].includes(user?.role);
  if (!ok) return <div className="p-8 text-sm" data-testid="onboarding-denied">Only admins can manage buyer onboarding.</div>;
  return <AdminOnboardingBody />;
}

function AdminOnboardingBody() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/onboardings").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);
  const waiting = data ? data.items.filter((r) => r.status === "submitted").length : 0;
  return (
    <div className="p-6 lg:p-8" data-testid="admin-onboarding-page">
      <div className="label-mono">Buyers</div>
      <h1 className="font-chivo font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none mb-2 flex items-center gap-3">
        <ClipboardText size={36} weight="bold" /> Buyer Onboarding
      </h1>
      <p className="text-sm text-zinc-700 max-w-3xl mb-5">
        Set the price, then send the buyer a private setup link (no login needed) or fill it in for them. When a buyer sends it back,
        press <b>Approve and go live</b>: we set up the buyer, their lead delivery, rules, thank-you page and their own branded pages in one go.
      </p>
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <button type="button" onClick={() => setOpen(true)} data-testid="onboarding-new"
                className="inline-flex items-center gap-2 bg-black text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors duration-100">
          <Plus size={14} weight="bold" /> New setup link
        </button>
        {waiting > 0 && <span className="label-mono text-amber-800 font-bold" data-testid="onboarding-waiting">{waiting} waiting for your approval</span>}
      </div>
      <TermsEditor onSaved={load} />
      {data === null && <div className="label-mono">Loading…</div>}
      {data === false && <div className="text-sm text-red-700">Couldn&apos;t load. Refresh to try again.</div>}
      {data && <OnboardingTable rows={data.items} onOpen={(id) => nav(`/admin/onboarding/${id}`)} />}
      <NewOnboardingDialog open={open} onOpenChange={setOpen} onCreated={load} onFillIn={(id) => nav(`/admin/onboarding/${id}`)} />
    </div>
  );
}

const copy = (path) => navigator.clipboard.writeText(fullLink(path)).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy"));

const OnboardingTable = ({ rows, onOpen }) => (!rows.length ? (
  <div className="text-sm text-zinc-600 border-2 border-dashed border-zinc-400 p-8 text-center" data-testid="onboarding-empty">
    No setup links yet. Press New setup link to start.
  </div>
) : (
  <div className="border-2 border-black bg-white overflow-x-auto" data-testid="onboarding-table">
    <table className="w-full text-sm">
      <thead className="bg-zinc-100 text-left">
        <tr>{["Buyer", "Price", "Status", "Updated", ""].map((h) => <th key={h} className="label-mono px-4 py-2">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`onboarding-row-${r.id}`}>
            <td className="px-4 py-3"><div className="font-bold">{r.firm_name || "Not named yet"}</div><div className="text-[11px] text-zinc-500">{r.contact_email}</div></td>
            <td className="px-4 py-3 font-mono">{money(r.price_per_lead)}</td>
            <td className="px-4 py-3"><StatusChip s={r.status} testId={`onboarding-status-${r.id}`} /></td>
            <td className="px-4 py-3 text-[11px] text-zinc-600">{new Date(r.went_live_at || r.submitted_at || r.created_at).toLocaleDateString()}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
              {r.status !== "live" && <button type="button" onClick={() => copy(r.link_path)} className="underline text-[11px] uppercase tracking-widest" data-testid={`onboarding-copy-${r.id}`}>Copy link</button>}
              <button type="button" onClick={() => onOpen(r.id)} data-testid={`onboarding-open-${r.id}`}
                      className="border-2 border-black px-3 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100">
                {r.status === "submitted" ? "Review" : r.status === "live" ? "View" : "Fill in"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
));
